import type { QueryKey } from "@tanstack/react-query";
import {
  isObject,
  pagesOf,
  type CacheEntry,
  type CacheSlot,
  type RecordChange,
} from "./cacheSlots";
import {
  includes,
  opaqueChanged,
  proven,
  sameFilters,
  type Filters,
  type ListView,
  type ViewRules,
} from "./listViews";

/** The rows leaving and entering one aggregate's set, and the set's size when a list shows it. */
export interface AggregateDelta<R> {
  removed?: R;
  added?: R;
  size?: { before: number; after: number };
}

export type ApplyDelta<D, R> = (data: D, delta: AggregateDelta<R>) => D;

/**
 * Aggregates over the same filtered set as a list: a change is applied as the
 * difference between the record's contribution before and after it, only where
 * a list with the same non-patchable filters proves the record belongs.
 */
export function aggregateSlot<D, R, TId, TPatch>(def: {
  prefix: QueryKey;
  rules: ViewRules<R>;
  parse: (key: QueryKey) => Filters | null;
  isData: (value: unknown) => value is D;
  apply: ApplyDelta<D, R>;
  sizeFrom?: {
    prefix: QueryKey;
    parse: (key: QueryKey) => ListView | null;
    total: (page: unknown) => number | undefined;
  };
}): CacheSlot<TId, TPatch, R> {
  const sizeOf = (change: RecordChange<R>, filters: Filters, countDelta: number) => {
    const source = def.sizeFrom;
    if (!source) return undefined;
    for (const [key, data] of change.read(source.prefix)) {
      const view = source.parse(key);
      if (!view || !sameFilters(view.filters, filters)) continue;
      const first = pagesOf(data, (value): value is unknown => isObject(value))?.[0];
      const after = first === undefined ? undefined : source.total(first);
      if (after !== undefined) return { before: after - countDelta, after };
    }
    return undefined;
  };

  const walk = (
    entries: readonly CacheEntry[],
    change: RecordChange<R>,
    removing: boolean,
  ): CacheEntry[] => {
    const { before } = change;
    const after = removing ? undefined : change.after;
    if (before === undefined || (!removing && after === undefined)) return [];
    const changed: CacheEntry[] = [];
    for (const [key, data] of entries) {
      const filters = def.parse(key);
      if (!filters || !def.isData(data)) continue;
      if (!proven(def.rules, change, filters)) continue;
      if (after !== undefined && opaqueChanged(def.rules, filters, before, after)) continue;
      const wasIn = includes(def.rules, filters, before);
      const isIn = after === undefined ? false : includes(def.rules, filters, after);
      if (wasIn === undefined || isIn === undefined || (!wasIn && !isIn)) continue;
      const next = def.apply(data, {
        removed: wasIn ? before : undefined,
        added: isIn ? after : undefined,
        size: sizeOf(change, filters, Number(isIn) - Number(wasIn)),
      });
      if (next !== data) changed.push([key, next]);
    }
    return changed;
  };

  return {
    prefix: def.prefix,
    patchAll: (entries, _id, _patch, change) => walk(entries, change, false),
    removeAll: (entries, _id, change) => walk(entries, change, true),
  };
}

type Flat = Record<string, unknown>;

const CENTS = 100;

function cents(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number * CENTS) : null;
}

function signed<R>(delta: AggregateDelta<R>, read: (row: R) => number): number {
  return (delta.added ? read(delta.added) : 0) - (delta.removed ? read(delta.removed) : 0);
}

function asDecimal(previous: unknown, amount: number): unknown {
  const text = (amount / CENTS).toFixed(2);
  return typeof previous === "number" ? Number(text) : text;
}

function roundedPercent(part: number, whole: number): string {
  return (Math.floor((2 * part * 10_000 + whole) / (2 * whole)) / CENTS).toFixed(2);
}

const RATIO_EXACT_UP_TO = 10_000;

/**
 * `{ "field:fn": value }` maps as the list endpoints answer them. Sums and
 * counts move by the delta; a distribution drops a key at zero and takes the
 * backend's empty value (null unless given); a percentage recovers its
 * numerator from the set's size, which is exact below ten thousand rows.
 * Averages, extremes and unique counts are never touched.
 */
export function flatAggregates<R>(def: {
  sums?: Readonly<Record<string, (row: R) => unknown>>;
  counts?: Readonly<Record<string, (row: R) => boolean>>;
  distributions?: Readonly<Record<string, (row: R) => readonly string[]>>;
  ratios?: Readonly<Record<string, (row: R) => boolean>>;
  emptyDistribution?: Readonly<Record<string, number>> | null;
}): { isData: (value: unknown) => value is Flat; apply: ApplyDelta<Flat, R> } {
  const apply: ApplyDelta<Flat, R> = (data, delta) => {
    const next: Flat = { ...data };
    let changed = false;
    const set = (pair: string, value: unknown) => {
      if (next[pair] === value) return;
      next[pair] = value;
      changed = true;
    };
    for (const [pair, read] of Object.entries(def.sums ?? {})) {
      if (!(pair in data)) continue;
      const amount = signed(delta, (row) => cents(read(row)) ?? 0);
      if (amount !== 0) set(pair, asDecimal(data[pair], (cents(data[pair]) ?? 0) + amount));
    }
    for (const [pair, read] of Object.entries(def.counts ?? {})) {
      const count = signed(delta, (row) => Number(read(row)));
      if (pair in data && typeof data[pair] === "number" && count !== 0)
        set(pair, (data[pair] as number) + count);
    }
    for (const [pair, read] of Object.entries(def.distributions ?? {})) {
      if (!(pair in data)) continue;
      const buckets: Record<string, number> = isObject(data[pair])
        ? { ...(data[pair] as Record<string, number>) }
        : {};
      for (const bucket of delta.removed ? read(delta.removed) : [])
        buckets[bucket] = (buckets[bucket] ?? 0) - 1;
      for (const bucket of delta.added ? read(delta.added) : [])
        buckets[bucket] = (buckets[bucket] ?? 0) + 1;
      const kept = Object.entries(buckets).filter(([, count]) => count > 0);
      const value = kept.length === 0 ? (def.emptyDistribution ?? null) : Object.fromEntries(kept);
      if (JSON.stringify(value) !== JSON.stringify(data[pair] ?? null)) set(pair, value);
    }
    for (const [pair, read] of Object.entries(def.ratios ?? {})) {
      const { size } = delta;
      if (!(pair in data) || !size || size.before >= RATIO_EXACT_UP_TO) continue;
      const previous = data[pair] === null ? 0 : Number(data[pair]);
      const part =
        Math.round((previous * size.before) / CENTS) + signed(delta, (row) => Number(read(row)));
      set(pair, size.after === 0 ? null : roundedPercent(part, size.after));
    }
    return changed ? next : data;
  };
  return { isData: (value): value is Flat => isObject(value) && !Array.isArray(value), apply };
}
